var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { Frequency, RRule } from 'rrule';
import moment from 'moment';
var RRULE_WEEKDAY_TO_DAY = {
    0: 'MONDAY',
    1: 'TUESDAY',
    2: 'WEDNESDAY',
    3: 'THURSDAY',
    4: 'FRIDAY',
    5: 'SATURDAY',
    6: 'SUNDAY',
};
/**
 * Maps DayOfWeek string to RRule weekday number
 * RRule uses: 0=Monday, 1=Tuesday, 2=Wednesday, 3=Thursday, 4=Friday, 5=Saturday, 6=Sunday
 */
export var dayOfWeekToRRuleWeekday = function (day) {
    var mapping = {
        MONDAY: 0,
        TUESDAY: 1,
        WEDNESDAY: 2,
        THURSDAY: 3,
        FRIDAY: 4,
        SATURDAY: 5,
        SUNDAY: 6,
    };
    return mapping[day];
};
/**
 * Maps RecurrenceRule string to RRule Frequency enum
 */
export var recurrenceRuleToFrequency = function (rule) {
    switch (rule) {
        case 'DAILY':
            return Frequency.DAILY;
        case 'WEEKLY':
        case 'WEEKLY_ON_DAY':
        case 'WEEKDAY':
        case 'MOWEFR':
        case 'TUTH':
            return Frequency.WEEKLY;
        case 'MONTHLY':
        case 'MONTHLY_ON_DAY':
            return Frequency.MONTHLY;
        case 'YEARLY':
            return Frequency.YEARLY;
        case 'DO_NOT_REPEAT':
        case 'CUSTOM':
            return null;
        default: {
            var _exhaustiveCheck = rule;
            void _exhaustiveCheck;
            return null;
        }
    }
};
/**
 * Maps RecurrenceInterval string to RRule Frequency enum
 */
export var intervalToFrequency = function (interval) {
    switch (interval) {
        case 'DAY':
            return Frequency.DAILY;
        case 'WEEK':
            return Frequency.WEEKLY;
        case 'MONTHLY':
            return Frequency.MONTHLY;
        case 'YEARLY':
            return Frequency.YEARLY;
        default: {
            var _exhaustiveCheck = interval;
            return _exhaustiveCheck;
        }
    }
};
/**
 * Converts an RRule instance to a RecurrenceRule option.
 * Uses the rule's freq, interval, and byweekday to pick the best-matching preset.
 * Returns 'CUSTOM' when the rule doesn't match any preset (e.g. custom interval or days).
 */
export var rruleToRecurrenceRule = function (rrule) {
    var _a, _b;
    if (!rrule) {
        return 'DO_NOT_REPEAT';
    }
    var opts = rrule.options;
    var freq = opts.freq;
    var interval = (_a = opts.interval) !== null && _a !== void 0 ? _a : 1;
    var byweekday = ((_b = opts.byweekday) === null || _b === void 0 ? void 0 : _b.length)
        ? __spreadArray([], __read(opts.byweekday), false).sort(function (a, b) { return a - b; })
        : null;
    if (freq === Frequency.DAILY) {
        return interval === 1 ? 'DAILY' : 'CUSTOM';
    }
    if (freq === Frequency.WEEKLY) {
        if (interval !== 1) {
            return 'CUSTOM';
        }
        if (!byweekday || byweekday.length === 0) {
            return 'WEEKLY';
        }
        if (byweekday.length === 1) {
            return 'WEEKLY_ON_DAY';
        }
        var wdKey = byweekday.join(',');
        if (wdKey === '0,1,2,3,4')
            return 'WEEKDAY';
        if (wdKey === '0,2,4')
            return 'MOWEFR';
        if (wdKey === '1,3')
            return 'TUTH';
        return 'CUSTOM';
    }
    if (freq === Frequency.MONTHLY) {
        if (interval !== 1) {
            return 'CUSTOM';
        }
        return (byweekday === null || byweekday === void 0 ? void 0 : byweekday.length) ? 'MONTHLY_ON_DAY' : 'MONTHLY';
    }
    if (freq === Frequency.YEARLY) {
        return interval === 1 ? 'YEARLY' : 'CUSTOM';
    }
    return 'CUSTOM';
};
/**
 * Gets byweekday array based on recurrence rule and selected weekdays
 * RRule uses: 0=Monday, 1=Tuesday, 2=Wednesday, 3=Thursday, 4=Friday, 5=Saturday, 6=Sunday
 */
export var getByWeekday = function (rule, selectedWeekdays, dayOfWeek) {
    switch (rule) {
        case 'DO_NOT_REPEAT':
        case 'DAILY':
        case 'WEEKLY':
        case 'MONTHLY':
        case 'YEARLY':
        case 'MONTHLY_ON_DAY':
            return null;
        case 'WEEKDAY':
            return [0, 1, 2, 3, 4]; // Monday to Friday
        case 'MOWEFR':
            return [0, 2, 4]; // Monday, Wednesday, Friday
        case 'TUTH':
            return [1, 3]; // Tuesday, Thursday
        case 'WEEKLY_ON_DAY':
            return [
                dayOfWeekToRRuleWeekday(dayOfWeek.format('dddd').toUpperCase()),
            ];
        case 'CUSTOM':
            return selectedWeekdays.length > 0
                ? selectedWeekdays.map(dayOfWeekToRRuleWeekday)
                : null;
        default: {
            var _exhaustiveCheck = rule;
            return _exhaustiveCheck;
        }
    }
};
export var getRecurrenceKey = function (rule) {
    if (!rule) {
        return null;
    }
    return rule.toString();
};
export function isNeverEndingRRule(rrule) {
    if (!rrule) {
        return false;
    }
    var opts = rrule.options;
    return !opts.until && !opts.count;
}
function frequencyToInterval(freq) {
    switch (freq) {
        case Frequency.DAILY:
            return 'DAY';
        case Frequency.WEEKLY:
            return 'WEEK';
        case Frequency.MONTHLY:
            return 'MONTHLY';
        case Frequency.YEARLY:
            return 'YEARLY';
        default:
            return 'WEEK';
    }
}
function parseSelectedWeekdaysFromRRule(rrule) {
    var byweekday = rrule.options.byweekday;
    if (!(byweekday === null || byweekday === void 0 ? void 0 : byweekday.length)) {
        return [];
    }
    return byweekday
        .map(function (weekday) { return RRULE_WEEKDAY_TO_DAY[weekday]; })
        .filter(function (day) { return !!day; });
}
export function getDefaultPanelState(abtUntilDate) {
    return {
        recurrenceRule: 'DO_NOT_REPEAT',
        interval: 'WEEK',
        intervalValue: 1,
        selectedWeekdays: [],
        endOption: abtUntilDate ? 'onDate' : 'never',
        endDate: abtUntilDate ? abtUntilDate.clone() : null,
        occurrenceCount: 1,
        presetUnderAbt: null,
    };
}
/**
 * Hydrates RecurrenceDropdown local state from a saved RRule.
 * Saved until/count take precedence over ABT defaults.
 */
export function rruleToPanelState(rrule, dateStart, abtUntilDate) {
    var _a;
    var opts = rrule.options;
    var preset = rruleToRecurrenceRule(rrule);
    var hasExplicitEnd = !!(opts.until || opts.count);
    var customPresetState = preset !== 'DO_NOT_REPEAT' && preset !== 'CUSTOM'
        ? presetToCustomState(preset, dateStart)
        : null;
    var recurrenceRule = preset;
    var presetUnderAbt = null;
    if (hasExplicitEnd && preset !== 'DO_NOT_REPEAT') {
        recurrenceRule = 'CUSTOM';
    }
    else if (abtUntilDate &&
        preset !== 'DO_NOT_REPEAT' &&
        preset !== 'CUSTOM' &&
        isNeverEndingRRule(rrule)) {
        recurrenceRule = 'CUSTOM';
        presetUnderAbt = preset;
    }
    var endOption = 'never';
    var endDate = null;
    var occurrenceCount = 1;
    if (opts.until) {
        endOption = 'onDate';
        endDate = moment(opts.until);
    }
    else if (opts.count) {
        endOption = 'afterOccurrences';
        occurrenceCount = opts.count;
    }
    else if (abtUntilDate && recurrenceRule === 'CUSTOM') {
        endOption = 'onDate';
        endDate = abtUntilDate.clone();
    }
    if (customPresetState && recurrenceRule === 'CUSTOM') {
        return {
            recurrenceRule: 'CUSTOM',
            interval: customPresetState.interval,
            intervalValue: customPresetState.intervalValue,
            selectedWeekdays: customPresetState.selectedWeekdays,
            endOption: endOption,
            endDate: endDate,
            occurrenceCount: occurrenceCount,
            presetUnderAbt: presetUnderAbt,
        };
    }
    return {
        recurrenceRule: recurrenceRule,
        interval: frequencyToInterval(opts.freq),
        intervalValue: (_a = opts.interval) !== null && _a !== void 0 ? _a : 1,
        selectedWeekdays: parseSelectedWeekdaysFromRRule(rrule),
        endOption: endOption,
        endDate: endDate,
        occurrenceCount: occurrenceCount,
        presetUnderAbt: presetUnderAbt,
    };
}
/**
 * Translates a non-CUSTOM, non-DO_NOT_REPEAT preset into the equivalent
 * CUSTOM-panel state (interval / intervalValue / selectedWeekdays) so the
 * resulting RRule matches the original preset. Returns null for rules that
 * have no representation in the CUSTOM picker (DO_NOT_REPEAT, CUSTOM, YEARLY).
 *
 * Used when ABT is active: any preset selection is rewritten to CUSTOM so the
 * user sees an editable end-date control, prefilled to abtUntilDate.
 */
export var presetToCustomState = function (rule, dateStart) {
    switch (rule) {
        case 'DAILY':
            return { interval: 'DAY', intervalValue: 1, selectedWeekdays: [] };
        case 'WEEKLY':
            return { interval: 'WEEK', intervalValue: 1, selectedWeekdays: [] };
        case 'MONTHLY':
        case 'MONTHLY_ON_DAY':
            return { interval: 'MONTHLY', intervalValue: 1, selectedWeekdays: [] };
        case 'WEEKDAY':
            return {
                interval: 'WEEK',
                intervalValue: 1,
                selectedWeekdays: [
                    'MONDAY',
                    'TUESDAY',
                    'WEDNESDAY',
                    'THURSDAY',
                    'FRIDAY',
                ],
            };
        case 'MOWEFR':
            return {
                interval: 'WEEK',
                intervalValue: 1,
                selectedWeekdays: ['MONDAY', 'WEDNESDAY', 'FRIDAY'],
            };
        case 'TUTH':
            return {
                interval: 'WEEK',
                intervalValue: 1,
                selectedWeekdays: ['TUESDAY', 'THURSDAY'],
            };
        case 'WEEKLY_ON_DAY':
            return {
                interval: 'WEEK',
                intervalValue: 1,
                selectedWeekdays: [dateStart.format('dddd').toUpperCase()],
            };
        case 'DO_NOT_REPEAT':
        case 'CUSTOM':
        case 'YEARLY':
            return null;
        default: {
            var _exhaustiveCheck = rule;
            void _exhaustiveCheck;
            return null;
        }
    }
};
/**
 * Computes the maximum number of occurrences of a recurrence rule that fit
 * between dateStart (inclusive) and until (inclusive). Returns at least 1
 * even when until is before dateStart (so an InputNumber max stays usable).
 */
export var computeMaxOccurrencesUnder = function (_a) {
    var freq = _a.freq, interval = _a.interval, byweekday = _a.byweekday, dateStart = _a.dateStart, until = _a.until;
    if (until.isBefore(dateStart))
        return 1;
    if (byweekday && byweekday.length > 0 && freq === Frequency.WEEKLY) {
        var days = until.diff(dateStart, 'days');
        var weekdaySet = new Set(byweekday);
        var startWeek = dateStart.clone().startOf('isoWeek');
        var count = 0;
        for (var d = 0; d <= days; d++) {
            var current = dateStart.clone().add(d, 'days');
            var weekOffset = current
                .clone()
                .startOf('isoWeek')
                .diff(startWeek, 'weeks');
            if (weekOffset % interval !== 0)
                continue;
            var dow = current.day();
            // moment uses Sun=0, rrule uses Mon=0
            var rruleDow = (dow + 6) % 7;
            if (weekdaySet.has(rruleDow))
                count++;
        }
        return Math.max(1, count);
    }
    var span;
    switch (freq) {
        case Frequency.DAILY:
            span = until.diff(dateStart, 'days');
            break;
        case Frequency.WEEKLY:
            span = until.diff(dateStart, 'weeks');
            break;
        case Frequency.MONTHLY:
            span = until.diff(dateStart, 'months');
            break;
        case Frequency.YEARLY:
            span = until.diff(dateStart, 'years');
            break;
        default:
            span = until.diff(dateStart, 'days');
            break;
    }
    return Math.max(1, Math.floor(span / interval) + 1);
};
export function pinEndToAbt(until) {
    return { endOption: 'onDate', endDate: until.clone() };
}
export function panelStateFromPresetUnderAbt(rule, dateStart, until, presetUnderAbt) {
    var custom = presetToCustomState(rule, dateStart);
    if (!custom) {
        return null;
    }
    return __assign(__assign({ recurrenceRule: 'CUSTOM', presetUnderAbt: presetUnderAbt, occurrenceCount: 1 }, custom), pinEndToAbt(until));
}
export function selectPreset(panel, value, dateStart, abtUntilDate) {
    if (abtUntilDate && value !== 'CUSTOM' && value !== 'DO_NOT_REPEAT') {
        var converted = panelStateFromPresetUnderAbt(value, dateStart, abtUntilDate, value);
        if (converted) {
            return converted;
        }
    }
    return __assign(__assign({}, panel), { presetUnderAbt: null, recurrenceRule: value });
}
export function patchCustomPanel(panel, patch) {
    return __assign(__assign(__assign({}, panel), patch), { presetUnderAbt: null });
}
export function clearPresetWhenAbtClears(panel, abtUntilDate) {
    if (!abtUntilDate && panel.presetUnderAbt !== null) {
        return __assign(__assign({}, panel), { presetUnderAbt: null });
    }
    return null;
}
/**
 * When ABT is active, rewrite never-ending rules to CUSTOM with an until date.
 */
export function enforceNeverEndingUnderAbt(panel, ruleToFix, dateStart, abtUntilDate) {
    if (panel.recurrenceRule === 'DO_NOT_REPEAT') {
        return null;
    }
    if (!isNeverEndingRRule(ruleToFix)) {
        return null;
    }
    var preset = rruleToRecurrenceRule(ruleToFix);
    if (preset === 'DO_NOT_REPEAT') {
        return null;
    }
    if (preset === 'CUSTOM') {
        if (panel.endOption === 'never') {
            return __assign(__assign({}, panel), pinEndToAbt(abtUntilDate));
        }
        return null;
    }
    return panelStateFromPresetUnderAbt(preset, dateStart, abtUntilDate, preset);
}
/**
 * When the ABT window changes (e.g. spaces added/removed), clamp Custom-panel
 * end date and occurrence count to stay within the new window.
 */
export function reconcileAbtDateChange(panel, abtUntilDate, prevAbtUntilDate, dateStart, ruleToFix, maxOccurrencesUnderAbt) {
    var _a;
    if (panel.recurrenceRule === 'DO_NOT_REPEAT') {
        return null;
    }
    var prevMs = (_a = prevAbtUntilDate === null || prevAbtUntilDate === void 0 ? void 0 : prevAbtUntilDate.clone().startOf('day').valueOf()) !== null && _a !== void 0 ? _a : null;
    var nextMs = abtUntilDate.clone().startOf('day').valueOf();
    if (prevMs === nextMs) {
        return null;
    }
    if (panel.recurrenceRule !== 'CUSTOM') {
        if (ruleToFix && isNeverEndingRRule(ruleToFix)) {
            var preset = rruleToRecurrenceRule(ruleToFix);
            if (preset !== 'DO_NOT_REPEAT' && preset !== 'CUSTOM') {
                return panelStateFromPresetUnderAbt(preset, dateStart, abtUntilDate, preset);
            }
        }
        return null;
    }
    if (panel.endOption === 'onDate') {
        var endDate = panel.endDate;
        if (!endDate || endDate.isAfter(abtUntilDate, 'day')) {
            return __assign(__assign({}, panel), { endDate: abtUntilDate.clone() });
        }
        if (prevAbtUntilDate &&
            endDate.isSame(prevAbtUntilDate, 'day') &&
            abtUntilDate.isAfter(prevAbtUntilDate, 'day')) {
            return __assign(__assign({}, panel), { endDate: abtUntilDate.clone() });
        }
        return null;
    }
    if (panel.endOption === 'afterOccurrences') {
        if (maxOccurrencesUnderAbt !== undefined &&
            panel.occurrenceCount > maxOccurrencesUnderAbt) {
            return __assign(__assign({}, panel), { occurrenceCount: maxOccurrencesUnderAbt });
        }
        return null;
    }
    if (panel.endOption === 'never') {
        return __assign(__assign({}, panel), pinEndToAbt(abtUntilDate));
    }
    return null;
}
export function panelToRRule(panel, dateStart, byweekday) {
    var recurrenceRule = panel.recurrenceRule, interval = panel.interval, intervalValue = panel.intervalValue, selectedWeekdays = panel.selectedWeekdays, endOption = panel.endOption, endDate = panel.endDate, occurrenceCount = panel.occurrenceCount;
    if (recurrenceRule === 'DO_NOT_REPEAT') {
        return null;
    }
    var freq = recurrenceRule === 'CUSTOM'
        ? intervalToFrequency(interval)
        : recurrenceRuleToFrequency(recurrenceRule);
    if (!freq) {
        return null;
    }
    var resolvedByweekday = byweekday !== null && byweekday !== void 0 ? byweekday : getByWeekday(recurrenceRule, selectedWeekdays, dateStart);
    var options = {
        freq: freq,
        interval: intervalValue,
    };
    if (resolvedByweekday && resolvedByweekday.length > 0) {
        options.byweekday = resolvedByweekday;
    }
    if (recurrenceRule === 'CUSTOM') {
        if (endOption === 'onDate' && endDate) {
            options.until = endDate.toDate();
        }
        else if (endOption === 'afterOccurrences') {
            options.count = occurrenceCount;
        }
    }
    return new RRule(options);
}
